/**
 * Authentication Context
 * Sole handler of token storage and auth state.
 */

import React, { createContext, useState, useEffect, useCallback } from 'react';
import authService from '@services/authService';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken'));
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const getStorage = useCallback(() => {
    return localStorage.getItem('rememberMe') === 'true' ? localStorage : sessionStorage;
  }, []);

  // Restore session on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        const storedToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
        if (storedToken) {
          const response = await authService.getProfile();
          if (response.success) {
            setToken(storedToken);
            setUser(response.data?.user || response.data);
            setIsAuthenticated(true);
          }
        }
      } catch {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        sessionStorage.removeItem('accessToken');
        sessionStorage.removeItem('refreshToken');
        setToken(null);
      } finally {
        setIsLoading(false);
      }
    };
    initAuth();
  }, []);

  const login = useCallback(async (email, password, rememberMe = false) => {
    // authService.login returns { success, data: { user, accessToken, refreshToken } }
    // OR — when 2FA is enabled on the account — { success: true, data:
    // { email, requires_2fa: true } } with NO tokens. In the latter case
    // we return up to the caller so it can show the OTP step instead of
    // treating it as a completed sign-in.
    const response = await authService.login({ email, password });

    // Read BOTH spellings. api.js converts snake_case responses with
    // /_([a-z])/g, which needs a lowercase LETTER after the underscore — so
    // `requires_2fa` passes through unconverted while `requires_verification`
    // becomes `requiresVerification`. Checking only the camel form meant this
    // branch never ran: login fell through to the block below, stored
    // `undefined` as the access token, flipped isAuthenticated true, and the
    // dashboard immediately 401'd back to /login. Anyone who turned on 2FA was
    // locked out of their account.
    //
    // Deliberately NOT fixed by widening that regex: ~15 admin metric keys
    // (active_30d, signups_7d, trials_ending_3d, …) rely on passing through
    // unconverted, and EmailDeliverability.jsx + SubscriptionLifecycle.jsx read
    // them in exactly that form. Widening it would silently break both pages.
    const requiresTwoFactor = response.data?.requires2fa ?? response.data?.requires_2fa;

    if (response.success && requiresTwoFactor) {
      // Carry rememberMe through so the verify-2fa step persists it
      // when it finally stores the tokens. Also surface devOtp (only
      // populated on non-prod/non-stage envs) so the SPA can display
      // it on the 2FA step.
      return { success: true, requires2fa: true, email: response.data.email || email, rememberMe, devOtp: response.data.devOtp };
    }
    // A success response that carries no access token is not a completed
    // sign-in. Storing `undefined` here is what turned the 2FA bug above into
    // a redirect loop rather than a visible error, so guard the whole branch
    // instead of trusting `success` alone.
    if (response.success && response.data?.accessToken) {
      // Always use localStorage so tokens persist across tabs
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      sessionStorage.removeItem('accessToken');
      sessionStorage.removeItem('refreshToken');
      localStorage.setItem('accessToken', response.data.accessToken);
      localStorage.setItem('refreshToken', response.data.refreshToken);
      localStorage.setItem('rememberMe', String(rememberMe));
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }
      setToken(response.data.accessToken);
      setUser(response.data.user);
      setIsAuthenticated(true);
    }
    return response;
  }, []);

  const register = useCallback(async (userData) => {
    // Registration only creates the account and sends an OTP — no tokens issued yet.
    // Tokens are issued only after email verification via verifyEmail().
    return authService.register(userData);
  }, []);

  /**
   * Complete login after a 2FA prompt: verify the email OTP, persist
   * tokens, hydrate auth state. Mirrors what the normal login() does
   * once tokens come back.
   */
  const verify2faLogin = useCallback(async (email, otp, rememberMe = false) => {
    const response = await authService.verify2faLogin(email, otp);
    if (response.success && response.data?.accessToken) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      sessionStorage.removeItem('accessToken');
      sessionStorage.removeItem('refreshToken');
      localStorage.setItem('accessToken', response.data.accessToken);
      localStorage.setItem('refreshToken', response.data.refreshToken);
      localStorage.setItem('rememberMe', String(rememberMe));
      if (rememberMe) localStorage.setItem('rememberedEmail', email);
      else            localStorage.removeItem('rememberedEmail');
      setToken(response.data.accessToken);
      setUser(response.data.user);
      setIsAuthenticated(true);
      return response;
    }

    // Success without a token is not a sign-in. Returning the raw response
    // here would let Login.jsx redirect on `success` alone and bounce straight
    // back off the dashboard — the same loop the 2FA key mismatch caused.
    return {
      ...response,
      success: false,
      message: response.message || 'Sign-in could not be completed. Please try again.',
    };
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('rememberMe');
      sessionStorage.removeItem('accessToken');
      sessionStorage.removeItem('refreshToken');
      // The service worker caches authenticated API GETs so they can be read
      // offline. Those must not survive the session — otherwise the next
      // person to use this browser could read the previous user's data.
      try {
        const worker = navigator.serviceWorker?.controller;
        if (worker) worker.postMessage({ type: 'CLEAR_API_CACHE' });
      } catch { /* no service worker — nothing cached */ }
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  const hasRole = useCallback((roles) => {
    if (!user) return false;
    return Array.isArray(roles) ? roles.includes(user.role) : user.role === roles;
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, setUser, token, isLoading, isAuthenticated, login, register, logout, hasRole, verify2faLogin }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
